import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CreditCard } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { readStripeConnectReturn, type StripeConnectAccount } from '@/lib/stripeConnect';
import { stripeConnectService } from '@/services/stripeConnect.service';

type LoadState = 'loading' | 'ready' | 'error';

interface StripeConnectRowProps {
  workspaceId?: number;
  // Seuls les administrateurs connectent ou déconnectent le compte (le back le vérifie aussi).
  canManage: boolean;
}

export default function StripeConnectRow({ workspaceId, canManage }: StripeConnectRowProps) {
  const { t } = useTranslation('integrations');
  const [searchParams, setSearchParams] = useSearchParams();
  const [account, setAccount] = useState<StripeConnectAccount | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [isBusy, setIsBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const stripeReturn = readStripeConnectReturn(searchParams);
    if (!stripeReturn) return;
    if (stripeReturn.result === 'connected') toast.success(t('stripe.toast.connected'));
    else toast.error(t(`stripe.returnError.${stripeReturn.reason}`));
    const next = new URLSearchParams(searchParams);
    next.delete('stripe');
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    stripeConnectService
      .getAccount(workspaceId)
      .then((next) => {
        if (cancelled) return;
        setAccount(next);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, reloadKey]);

  const handleConnect = async () => {
    if (!workspaceId) return;
    setIsBusy(true);
    try {
      window.location.assign(await stripeConnectService.createAuthorization(workspaceId));
    } catch {
      toast.error(t('stripe.toast.connectError'));
      setIsBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!workspaceId) return;
    setIsBusy(true);
    try {
      await stripeConnectService.disconnect(workspaceId);
      setAccount({ connected: false, stripeAccountId: null, displayName: null });
      toast.success(t('stripe.toast.disconnected'));
      setConfirmOpen(false);
    } catch {
      toast.error(t('stripe.toast.disconnectError'));
    } finally {
      setIsBusy(false);
    }
  };

  const isConnected = state === 'ready' && account?.connected === true;
  const status =
    state === 'error'
      ? { label: t('status.error'), variant: 'destructive' as const }
      : isConnected
        ? { label: t('status.active'), variant: 'live' as const }
        : { label: t('status.inactive'), variant: 'draft' as const };

  return (
    <TableRow>
      <TableCell className="flex items-center gap-2">
        <span className="size-8 shrink-0 flex items-center justify-center rounded-full bg-subtle">
          <CreditCard className="size-4 text-secondary" aria-hidden />
        </span>
        <span className="flex flex-col">
          <span>Stripe</span>
          {isConnected && account?.displayName && (
            <span className="text-xs text-secondary">{account.displayName}</span>
          )}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-3">
          {state !== 'loading' && <Badge variant={status.variant}>{status.label}</Badge>}
          {state === 'error' && (
            <Button variant="outline" size="sm" onClick={() => setReloadKey((key) => key + 1)}>
              {t('stripe.retry')}
            </Button>
          )}
          {state === 'ready' && canManage && !isConnected && (
            <Button size="sm" onClick={handleConnect} disabled={isBusy}>
              {t('stripe.connect')}
            </Button>
          )}
          {state === 'ready' && canManage && isConnected && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={isBusy}
            >
              {t('stripe.disconnect')}
            </Button>
          )}
        </div>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('stripe.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('stripe.confirmDescription')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isBusy}>{t('stripe.confirmCancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  void handleDisconnect();
                }}
                disabled={isBusy}
              >
                {t('stripe.confirmAction')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  );
}
